import { PAYMENT_FREQUENCY, SALARY_TYPES, EMPLOYEE_STATUS } from './constants';
import { any } from '../../shared/utils/array';
import getInputsByLocale from './getInputsByLocale';
import { formatCurrency } from 'src/shared/hooks/useCurrencyMask';
import { type Collective } from 'src/shared/types/collective';
import { CollectivesSelectField } from './components/CollectivesSelectField';
import { WorkingTimeField } from './components/WorkingTimeField';

type Fields = {
  firstName: string;
  lastName: string;
  payrollId?: string;
  personalId?: string;
  email?: string;
  phone?: string;
  iban?: string;
  bankAccountNumber?: string;
  paymentFrequency?: string;
  startDate?: Date | null;
  contractEnd?: Date | null;
  salaryType?: string;
  salary?: number;
  status?: string;
  subsidiary?: string;
  collectiveIds?: string[];
  workingTimePercentage?: number | null;
};

type Company = {
  locale?: string;
  taxId?: string;
  legalName?: string;
  name?: string;
  flexFlow?: boolean;
};

type Subsidiary = {
  id: string;
  legalName: string;
  taxId: string;
};

const firstNameInputOptions = {
  name: 'firstName',
  label: 'first name',
  type: 'text'
};

const lastNameInputOptions = {
  name: 'lastName',
  label: 'last name',
  type: 'text'
};

const personalIdInputOptions = {
  name: 'personalId',
  label: 'personal id'
};

const payrollIdInputOptions = {
  name: 'payrollId',
  label: 'payroll id (optional)'
};

const emailInputOptions = {
  name: 'email',
  label: 'e-mail'
};

const phoneInputOptions = {
  name: 'phone',
  label: 'phone'
};

const ibanInputOptions = {
  name: 'iban',
  label: 'iban'
};

const bankAccountNumberInputOptions = {
  name: 'bankAccountNumber',
  label: 'bankAccountNumber'
};

const paymentFrequencyInputOptions = {
  name: 'paymentFrequency',
  label: 'payment frequency',
  select: true,
  options: Object.values(PAYMENT_FREQUENCY),
  disabled: true,
  md: 4
};

const employeeStatusInputOptions = (options: Subsidiary[]) => ({
  name: 'status',
  label: 'status',
  select: true,
  options: Object.values(EMPLOYEE_STATUS),
  md: any(options) ? 4 : 6
});

const subsidiaryInputOptions = (subsidiaries: Subsidiary[] | undefined) => ({
  name: 'subsidiary',
  label: 'subsidiary',
  options: subsidiaries?.map(subsidiary => ({
    value: subsidiary.id,
    label: subsidiary.legalName
  })),
  select: true,
  md: 4
});

const salaryInputOptions = (options: Subsidiary[]) => ({
  name: 'salary',
  label: 'salary',
  type: 'string',
  md: any(options) ? 4 : 6
});

const salaryTypeInputOptions = {
  name: 'salaryType',
  label: 'salary type',
  select: true,
  options: Object.values(SALARY_TYPES),
  md: 6
};

const startDateInputOptions = {
  name: 'startDate',
  label: 'contract start date',
  type: 'date',
  InputLabelProps: { shrink: true }
};

const contractEndInputOptions = {
  name: 'contractEnd',
  label: 'contract end date',
  type: 'date',
  InputLabelProps: { shrink: true },
  md: 6
};

const collectiveIdsInputOptions = (collectives: Collective[]) => ({
  name: 'collectiveIds',
  label: 'collectives',
  Tag: CollectivesSelectField,
  collectives,
  disabled: !collectives.length,
  md: 6
});

const workingTimePercentageInputOptions = {
  name: 'workingTimePercentage',
  label: 'working time',
  Tag: WorkingTimeField
};

const generateInput =
  (onChange: (event: React.ChangeEvent<HTMLInputElement>) => void, value: any) => (rest: Record<string, unknown>) => ({
    onChange,
    value,
    ...rest
  });

const generateInputs = (
  fields: Fields,
  onValuesChange: (event: { target: { name: string; value: unknown } }) => void,
  company: Company,
  subsidiaries: Subsidiary[],
  bankEntities: unknown,
  collectives: Collective[],
  isExtraPaymentEnabled: boolean
) => {
  const {
    firstName,
    lastName,
    payrollId,
    personalId,
    email,
    phone,
    iban,
    bankAccountNumber,
    paymentFrequency,
    startDate,
    contractEnd,
    salaryType,
    salary,
    status,
    subsidiary,
    collectiveIds,
    workingTimePercentage
  } = fields;

  const firstNameInput = generateInput(onValuesChange, firstName)(firstNameInputOptions);
  const lastNameInput = generateInput(onValuesChange, lastName)(lastNameInputOptions);
  const personalIdInput = generateInput(onValuesChange, personalId)(personalIdInputOptions);
  const payrollIdInput = generateInput(onValuesChange, payrollId)(payrollIdInputOptions);
  const emailInput = generateInput(onValuesChange, email)(emailInputOptions);
  const phoneInput = generateInput(onValuesChange, phone)(phoneInputOptions);
  const ibanInput = generateInput(onValuesChange, iban)(ibanInputOptions);
  const bankAccountInput = generateInput(onValuesChange, bankAccountNumber)(bankAccountNumberInputOptions);
  const salaryTypeInput = generateInput(onValuesChange, salaryType)(salaryTypeInputOptions);

  const salaryInput = generateInput(onValuesChange, formatCurrency(salary))(salaryInputOptions(subsidiaries));
  generateInput(onValuesChange, paymentFrequency)(paymentFrequencyInputOptions);
  const employeeStatusInput = generateInput(onValuesChange, status)(employeeStatusInputOptions(subsidiaries));
  const startDateInput = generateInput(onValuesChange, startDate)(startDateInputOptions);
  const contractEndInput = generateInput(onValuesChange, contractEnd)(contractEndInputOptions);
  const subsidiaryInput = generateInput(onValuesChange, subsidiary ?? '')(subsidiaryInputOptions(subsidiaries));
  const collectiveIdsInput = isExtraPaymentEnabled
    ? generateInput(onValuesChange, collectiveIds)(collectiveIdsInputOptions(collectives))
    : undefined;
  const workingTimePercentageInput = company?.flexFlow
    ? generateInput(onValuesChange, workingTimePercentage)(workingTimePercentageInputOptions)
    : undefined;

  const inputs = {
    firstNameInput,
    lastNameInput,
    personalIdInput,
    payrollIdInput,
    emailInput,
    phoneInput,
    bankAccountInput,
    ibanInput,
    salaryTypeInput,
    salaryInput,
    employeeStatusInput,
    startDateInput,
    contractEndInput,
    subsidiaryInput,
    collectiveIdsInput,
    workingTimePercentageInput
  };

  return getInputsByLocale(company?.locale, inputs, fields, onValuesChange, bankEntities);
};

export default generateInputs;
